import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Cookie, ScrollText, AlertTriangle } from 'lucide-react';

const LEGAL_PAGES = {
  'aviso-legal': {
    title: 'Aviso Legal (LSSI-CE)',
    icon: Shield,
    content: `
## 1. Datos Identificativos
En cumplimiento del artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), se informa que el titular de este portal es:

- **Denominación social:** AR Performance
- **Actividad:** Servicios de reprogramación y optimización de software automotriz
- **Contacto:** A través del formulario de contacto del portal

## 2. Objeto
Este portal proporciona servicios de modificación de software (firmware) para unidades de control electrónico (ECU) de vehículos, incluyendo pero no limitándose a: optimización de rendimiento (Stage 1, Stage 2), gestión de sistemas de emisiones, gestión de códigos de error y otras modificaciones bajo demanda.

## 3. Condiciones de Uso
El acceso y uso de este portal atribuye la condición de Usuario y supone la aceptación de todas las condiciones incluidas en este Aviso Legal, así como en la Política de Privacidad, Política de Cookies y las Condiciones Generales de Contratación.

## 4. Propiedad Intelectual e Industrial
Todos los contenidos del portal (textos, imágenes, software, código fuente, diseño gráfico, marcas, logotipos) son propiedad de AR Performance o de sus licenciantes y están protegidos por las leyes de propiedad intelectual e industrial. Queda prohibida su reproducción, distribución o transformación sin autorización previa.

## 5. Responsabilidad
AR Performance no se responsabiliza de:
- Posibles daños derivados de interferencias, interrupciones, virus informáticos, averías telefónicas o desconexiones del sistema.
- Del uso que los Usuarios hagan de los archivos y modificaciones obtenidos a través de la plataforma.
- De la adecuación de las modificaciones realizadas a la normativa específica de cada país.

## 6. Legislación Aplicable y Jurisdicción
Las relaciones entre AR Performance y los Usuarios se rigen por la legislación española vigente. Para la resolución de cualquier controversia, las partes se someterán a los Juzgados y Tribunales del domicilio del Usuario, si este tiene la condición de consumidor, o al domicilio social de AR Performance en otro caso.
    `,
  },
  'privacidad': {
    title: 'Política de Privacidad (RGPD y LOPDGDD)',
    icon: FileText,
    content: `
## 1. Responsable del Tratamiento
- **Responsable:** AR Performance
- **Finalidad:** Gestión de la relación comercial, prestación de servicios solicitados, facturación y comunicaciones relacionadas con el servicio.
- **Base legal:** Ejecución de contrato, consentimiento del interesado y cumplimiento de obligaciones legales.

## 2. Datos Recogidos
Recogemos los siguientes datos personales:
- Datos identificativos: nombre/empresa, email, teléfono.
- Datos fiscales: CIF/NIF, dirección fiscal (si procede).
- Datos técnicos: información del vehículo, archivos de firmware.
- Datos de uso: registros de actividad en la plataforma, historial de solicitudes.

## 3. Destinatarios
Los datos no serán cedidos a terceros, salvo obligación legal. Pueden tener acceso:
- Proveedores de servicios técnicos necesarios para la prestación del servicio.
- Administraciones públicas cuando exista obligación legal.

## 4. Plazo de Conservación
Los datos se conservarán mientras se mantenga la relación comercial y, posteriormente, durante los plazos de prescripción legal aplicables (generalmente 5 años para obligaciones fiscales).

## 5. Derechos del Interesado
El Usuario puede ejercer los siguientes derechos:
- **Acceso:** Conocer qué datos personales tratamos.
- **Rectificación:** Modificar datos inexactos o incompletos.
- **Supresión:** Solicitar la supresión de sus datos.
- **Oposición:** Oponerse al tratamiento de sus datos.
- **Portabilidad:** Obtener sus datos en formato electrónico.
- **Limitación:** Solicitar la limitación del tratamiento.

Para ejercer estos derechos, el Usuario puede dirigirse a través del portal o por email.

## 6. Medidas de Seguridad
AR Performance ha adoptado las medidas técnicas y organizativas necesarias para garantizar la seguridad de los datos personales, incluyendo cifrado de comunicaciones, control de acceso y copias de seguridad periódicas.

## 7. Transferencias Internacionales
Los datos pueden ser procesados por proveedores de servicios ubicados fuera del EEE, siempre bajo las garantías adecuadas conforme al RGPD (cláusulas contractuales tipo o decisiones de adecuación).
    `,
  },
  'cookies': {
    title: 'Política de Cookies',
    icon: Cookie,
    content: `
## 1. ¿Qué son las Cookies?
Las cookies son pequeños archivos de texto que se almacenan en su dispositivo al visitar una página web. Permiten recordar sus preferencias y mejorar la experiencia de navegación.

## 2. Cookies Utilizadas

### Cookies Técnicas (Necesarias)
| Cookie | Finalidad | Duración |
|--------|-----------|----------|
| token | Autenticación de sesión | Sesión |
| user | Datos de usuario en caché | Sesión |

### Cookies de Terceros
| Cookie | Proveedor | Finalidad | Duración |
|--------|-----------|-----------|----------|
| cf_clearance | Cloudflare | Verificación de seguridad (captcha) | 30 min |
| __cf_bm | Cloudflare | Gestión de bots | 30 min |

## 3. Gestión de Cookies
Puede configurar su navegador para rechazar cookies o ser avisado cuando se instalen. Sin embargo, el rechazo de cookies técnicas impedirá el correcto funcionamiento del portal.

### Cómo desactivar cookies en los principales navegadores:
- **Chrome:** Configuración → Privacidad y seguridad → Cookies
- **Firefox:** Opciones → Privacidad y seguridad → Cookies
- **Safari:** Preferencias → Privacidad → Cookies

## 4. Actualización
Esta política puede ser actualizada periódicamente. La fecha de última revisión se indica al pie de esta página.

*Última actualización: Enero 2026*
    `,
  },
  'condiciones': {
    title: 'Condiciones Generales de Contratación (CGC)',
    icon: ScrollText,
    content: `
## 1. Objeto
Las presentes CGC regulan la relación comercial entre AR Performance y los Usuarios profesionales que contratan servicios de modificación de firmware a través de la plataforma.

## 2. Servicios
Los servicios incluyen:
- Modificación de parámetros de software de ECU según las especificaciones solicitadas.
- Almacenamiento y gestión de archivos originales y modificados.
- Soporte técnico asociado a las modificaciones realizadas.

## 3. Proceso de Contratación
1. El Usuario se registra y accede al portal.
2. Selecciona los servicios deseados y sube el archivo original.
3. Confirma la solicitud y se procede al cargo de créditos.
4. AR Performance procesa la solicitud y entrega el archivo modificado.

## 4. Precios y Forma de Pago
- Los precios se expresan en créditos (equivalentes a euros).
- El pago se realiza mediante la adquisición previa de créditos.
- Los precios pueden variar según el tipo de vehículo (turismo/industrial) y el servicio solicitado.
- Se cobra el precio del servicio más caro cuando se solicitan múltiples servicios simultáneamente.

## 5. Entrega
- El plazo habitual de entrega es de 24 horas laborables desde la recepción del archivo.
- Los archivos se entregan a través de la plataforma.

## 6. Garantía
- AR Performance garantiza que el archivo modificado cumple con las especificaciones solicitadas.
- Se ofrece soporte técnico para cualquier incidencia relacionada con la modificación.
- La garantía no cubre daños derivados de una instalación incorrecta por parte del Usuario.

## 7. Desistimiento
Dado que los archivos modificados son productos personalizados creados específicamente para cada vehículo, no es aplicable el derecho de desistimiento una vez procesada la solicitud, conforme al artículo 103.c) del TRLGDCU.

## 8. Resolución de Conflictos
Para cualquier controversia, se aplicará la legislación española vigente.
    `,
  },
  'responsabilidad': {
    title: 'Cláusula de Exención de Responsabilidad y Uso de Archivos',
    icon: AlertTriangle,
    content: `
## 1. Responsabilidad Técnica
El servicio prestado consiste exclusivamente en la modificación de parámetros de software basados en la información proporcionada por el Usuario. AR Performance no se hace responsable de fallos mecánicos, daños en la unidad de control (ECU), roturas de motor o de cualquier componente periférico derivados de una instalación incorrecta o del estado previo del vehículo.

## 2. Homologación y Normativa
El Usuario es plenamente consciente de que las modificaciones solicitadas (como Stage 1, 2, gestión de sistemas DPF/EGR/AdBlue, etc.) pueden alterar las emisiones y especificaciones técnicas aprobadas por el fabricante. Es responsabilidad exclusiva del Usuario y/o del cliente final proceder a la homologación del vehículo ante las autoridades competentes (ITV) y cumplir con la normativa de circulación vigente. AR Performance no asume responsabilidad alguna por sanciones administrativas o denegaciones de inspección técnica.

## 3. Garantía del Vehículo
Se informa al Usuario que la manipulación del software original puede conllevar la pérdida total o parcial de la garantía oficial del fabricante del vehículo.

## 4. Uso de los Archivos
Los archivos suministrados son para uso exclusivo en el vehículo identificado por el Usuario. El uso de estos archivos en vehículos distintos o en condiciones no especificadas anula cualquier soporte técnico y responsabilidad de la plataforma.

## 5. Aceptación
Al utilizar los servicios de AR Performance, el Usuario declara conocer y aceptar íntegramente las condiciones descritas en esta cláusula. El uso de la plataforma implica la aceptación expresa de los términos aquí recogidos.
    `,
  },
};

const LegalPage = () => {
  const { page } = useParams();
  const pageData = LEGAL_PAGES[page];

  if (!pageData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Página no encontrada</h1>
          <Link to="/" className="text-primary hover:text-primary/80">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  const Icon = pageData.icon;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />Volver al inicio
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-white">{pageData.title}</h1>
        </div>

        <div className="prose prose-invert prose-zinc max-w-none">
          {pageData.content.split('\n').map((line, i) => {
            if (line.startsWith('## ')) return <h2 key={i} className="font-heading text-xl font-bold text-white mt-8 mb-3">{line.replace('## ', '')}</h2>;
            if (line.startsWith('### ')) return <h3 key={i} className="font-heading text-lg font-bold text-zinc-300 mt-6 mb-2">{line.replace('### ', '')}</h3>;
            if (line.startsWith('- **')) {
              const parts = line.replace('- **', '').split('**');
              return <p key={i} className="text-zinc-400 text-sm ml-4 mb-1"><strong className="text-zinc-200">{parts[0]}</strong>{parts[1]}</p>;
            }
            if (line.startsWith('- ')) return <p key={i} className="text-zinc-400 text-sm ml-4 mb-1">• {line.replace('- ', '')}</p>;
            if (line.startsWith('|')) return null; // Skip table lines, rendered below
            if (line.trim() === '') return null;
            if (line.startsWith('*')) return <p key={i} className="text-zinc-600 text-xs italic mt-4">{line.replace(/\*/g, '')}</p>;
            return <p key={i} className="text-zinc-400 text-sm leading-relaxed mb-3">{line.trim()}</p>;
          })}
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex items-center justify-between text-zinc-600 text-xs">
          <span>© 2026 AR Performance. Todos los derechos reservados.</span>
          <div className="flex gap-3">
            {Object.entries(LEGAL_PAGES).filter(([k]) => k !== page).map(([key, val]) => (
              <Link key={key} to={`/legal/${key}`} className="hover:text-zinc-400">{val.title.split(' (')[0]}</Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
